import React, { useState } from 'react';
import { logUserActivityQuietly } from './supabaseClient';

function TheophyllineCalculator({user}) {
    const [weight, setWeight] = useState('');
    const [height, setHeight] = useState('');
    const [gender, setGender] = useState('male');
    const [selectedCondition, setSelectedCondition] = useState('');
    const [selectedInducer, setSelectedInducer] = useState('');
    const [selectedInhibitor, setSelectedInhibitor] = useState(''); 
    const [indication, setIndication] = useState('');
    const [currentDose, setCurrentDose] = useState('');
    const [interval, setInterval] = useState('');
    const [saltType, setSaltType] = useState('');
    const [measuredLevel, setMeasuredLevel] = useState('');
    const [simDose, setSimDose] = useState('');
    const [simInterval, setSimInterval] = useState('');


    // 1. Define the factors from your handbook (TDM 8.png)
    const CL_FACTORS = {
        conditions: {
            "None": 1.0,
            "Smoking (tobacco)": 1.6,
            "Cystic fibrosis": 1.5,
            "Hyperthyroid": 1.2,
            "Severe COPD (with cor pulmonale)": 0.8,
            "Sepsis with multiorgan failure": 0.7,
            "Hypothyroid": 0.6,
            "Acute pulmonary edema": 0.5,
            "Acute viral illness (children 9-15y)": 0.5,
            "Hepatic cirrhosis / Acute hepatitis": 0.5,
            "Congestive heart failure": 0.5,
            "Pneumonia": 0.4
        },
        drugInducers: {
            "None": 1.0,
            "Phenytoin": 1.6,
            "Phenobarbitone": 1.3,
            "Rifampicin": 1.3
        },
        drugInhibitors: {
            "None": 1.0,
            "Allopurinol (≥600 mg/day)": 0.8,
            "Erythromycin": 0.75,
            "Ciprofloxacin": 0.7,
            "Frusemide": 0.7,
            "Ticlopidine": 0.65,
            "Cimetidine": 0.6,
            "Propranolol": 0.6,
            "Verapamil": 0.5,
            "Influenza vaccines": 0.5,
            "Fluvoxamine": 0.3
        }
    };

    // 2. Weight Selection & IBW Logic (Devine Formula)
    const weightNum = parseFloat(weight) || 0;
    const heightNum = parseFloat(height) || 0;

    let ibw = 0;
    if (heightNum > 0 && gender) {
        const inchesOver5Foot = Math.max(0, (heightNum / 2.54) - 60);
        ibw = gender === 'male' ? 50 + (2.3 * inchesOver5Foot) : 45.5 + (2.3 * inchesOver5Foot);
    }

    // Smart Selection: Use IBW if height exists and patient is overweight, otherwise use Actual Weight
    const weightToUse = (ibw > 0 && weightNum > ibw) ? ibw : weightNum;
    const isUsingIBW = ibw > 0 && weightNum > ibw;

    // 3. Multiplicative Clearance Math
    const condFactor = CL_FACTORS.conditions[selectedCondition] || 1.0;
    const inducerFactor = CL_FACTORS.drugInducers[selectedInducer] || 1.0;
    const inhibitorFactor = CL_FACTORS.drugInhibitors[selectedInhibitor] || 1.0;

    // Total Multiplier chain as instructed by your manual
    const totalMultiplier = condFactor * inducerFactor * inhibitorFactor;
    const finalCL = 0.04 * weightToUse * totalMultiplier;
    
    // Step 2 Indication Ranges based on Handbook Target (5 - 20 mg/L)
    const THEOPHYLLINE_TARGETS = {
        "General / Standard Therapeutic Range": "5 - 20",
        "Chronic Asthma Management / COPD Maintenance": "5 - 15",
        "Acute Bronchospasm / Asthma Exacerbation": "10 - 20",
        "Neonatal Apnea": "5 - 12"
    };

    // Helper function to fetch the range string for display
    const getTargetRange = () => {
        return THEOPHYLLINE_TARGETS[indication] || "5 - 20";
    };

    // Step 3 Variables & Guards
    const currentDoseNum = parseFloat(currentDose) || 0;
    const intervalNum = parseFloat(interval) || 12; // Default to 12h if empty
    const F = 1.0; // Bioavailability is always 1 as per handbook

    // Determine Salt Factor dynamically based on selection
    const S = saltType === 'aminophylline' ? 0.8 : 1.0;

    // Core Pharmacokinetic Formula: Css = (Dose * S * F) / (CL * Interval)
    const predictedTheoppCss = (finalCL > 0 && currentDoseNum > 0)
        ? (currentDoseNum * S * F) / (finalCL * intervalNum)
        : 0;

    // Extract Min/Max from Step 2 string to determine clinical suitability
    const checkTheoppRangeStatus = () => {
        const rangeStr = getTargetRange(); // e.g., "5 - 15"
        if (rangeStr.includes(" - ")) {
            const [min, max] = rangeStr.split(" - ").map(Number);
            if (predictedTheoppCss < min) return { label: "SUBTHERAPEUTIC", color: "#3498db" };
            if (predictedTheoppCss > max) return { label: "SUPRATHERAPEUTIC", color: "#e74c3c" };
            return { label: "WITHIN TARGET", color: "#2ecc71" };
        }
        return { label: "CHECK RANGE", color: "#95a5a6" };
    };

    const suitability = predictedTheoppCss > 0 ? checkTheoppRangeStatus() : { label: "WAITING INPUT", color: "#7f8c8d" };

    // Step 4 Variables & Back-Calculations
    const actualLevelNum = parseFloat(measuredLevel) || 0;

    // 1. Calculate Metabolism Deviation (%)
    const theoppDeviation = (predictedTheoppCss > 0 && actualLevelNum > 0)
        ? ((actualLevelNum - predictedTheoppCss) / predictedTheoppCss) * 100
        : 0;

    // 2. Back-calculate Individual Clearance (L/hr)
    // Formula derived from: Css = (Dose * S * F) / (CL * Interval)
    // Therefore: CL_indiv = (Dose * S * F) / (ActualCss * Interval)
    const individualTheoppCL = (actualLevelNum > 0 && currentDoseNum > 0)
        ? (currentDoseNum * S * F) / (actualLevelNum * intervalNum)
        : 0;

    // 3. Determine Metabolism Categorization
    const getTheoppMetabolismStatus = () => {
        if (theoppDeviation > 25) {
            return { 
                text: "Slow Metabolizer (or High Compliance/Early Sample)", 
                color: "#e67e22", 
                note: "The patient clears the drug slower than expected. Watch for acute toxicity or unrecorded enzyme inhibitors." 
            };
        }
        if (theoppDeviation < -25) {
            return { 
                text: "Fast Metabolizer (or Poor Compliance/Late Sample)", 
                color: "#3498db", 
                note: "The patient clears the drug faster than expected. Check compliance, smoking status, or concomitant enzyme inducers." 
            };
        }
        return { 
            text: "Normal Metabolizer", 
            color: "#2ecc71", 
            note: "The patient's metabolic rate closely matches the population baseline estimates." 
        };
    };

    const theoppMetabolism = actualLevelNum > 0 
        ? getTheoppMetabolismStatus() 
        : { text: "Awaiting Lab Level", color: "#7f8c8d", note: "Enter a measured level to analyze metabolic deviation." };

    // === RE-ENGINEERED DOSE SIMULATION CORE (INDIVIDUAL HIGH-PRECISION) ===
    const simDoseNum = parseFloat(simDose) || 0;
    const simIntervalNum = parseFloat(simInterval) || 12; // Default to 12h if empty

    let simulatedTheoppCss = 0;

    if (simDoseNum > 0 && currentDoseNum > 0 && actualLevelNum > 0) {
        // Individual linear ratio scaling that accounts for both dose shifts AND dosing interval changes!
        // Derived from: Css2 = (Dose2/Interval2) / (Dose1/Interval1) * Css1
        const currentRate = currentDoseNum / intervalNum;
        const proposedRate = simDoseNum / simIntervalNum;
        
        if (currentRate > 0) {
            simulatedTheoppCss = (proposedRate / currentRate) * actualLevelNum;
        }
    } else if (simDoseNum > 0 && finalCL > 0) {
        // Fallback to Multiplicative Population Clearance ONLY if no measured lab baseline exists yet
        simulatedTheoppCss = (simDoseNum * S * F) / (finalCL * simIntervalNum);
    }
    // Check if the simulated adjustment successfully hits the target range chosen in Step 2
    const checkSimulatedRangeStatus = (val) => {
        const rangeStr = getTargetRange();
        if (rangeStr.includes(" - ")) {
            const [min, max] = rangeStr.split(" - ").map(Number);
            if (val < min) return { label: "EXPECTED SUBTHERAPEUTIC", color: "#3498db" };
            if (val > max) return { label: "EXPECTED TOXIC RANGE", color: "#e74c3c" };
            return { label: "EXPECTED TARGET ACHIEVED", color: "#2ecc71" };
        }
        return { label: "CHECK TARGET", color: "#95a5a6" };
    };

    const simSuitability = simulatedTheoppCss > 0 
        ? checkSimulatedRangeStatus(simulatedTheoppCss) 
        : { label: "AWAITING SIMULATION INPUT", color: "#7f8c8d" };

    //Step 6:
    // 1. Define a single variable that EVERYONE in Step 6 can see!
    const activeTargetRange = getTargetRange(); // Grabs "5 - 20" or "5 - 15"

    // 2. Check suitability based entirely on the REAL lab result
    const checkActualRangeStatus = () => {
        if (activeTargetRange.includes(" - ")) {
            const [min, max] = activeTargetRange.split(" - ").map(Number);
            if (actualLevelNum < min) return "SUBTHERAPEUTIC";
            if (actualLevelNum > max) return "SUPRATHERAPEUTIC";
            return "WITHIN TARGET";
        }
        return "CHECK RANGE";
    };

    // 2. Get the real status of the patient's blood
    const actualStatus = actualLevelNum > 0 ? checkActualRangeStatus() : "PENDING";
    // 3. Dynamic header color selector based on reality status
    const getHeaderColor = () => {
        if (actualStatus === "WITHIN TARGET") return "#2ecc71";  // Green
        if (actualStatus === "SUBTHERAPEUTIC") return "#3498db"; // Blue
        if (actualStatus === "SUPRATHERAPEUTIC") return "#e74c3c"; // Red
        return "#7f8c8d";                                        // Grey fallback
    };

    // 4. Update Step 6 to evaluate 'actualStatus' instead of 'suitability.label'
    let theoppInterpretation = "";
    let theoppRecommendation = "";

    if (actualLevelNum > 0) {
        if (actualStatus === "WITHIN TARGET") {
            theoppInterpretation = `The measured level of ${actualLevelNum.toFixed(1)} mg/L falls safely within the optimal therapeutic window (${activeTargetRange} mg/L) designated for this indication.`;
            theoppRecommendation = "If response is poor, recheck other factor that may contribute to poor response (e.g, interacting medication, or changes their smoking habits). May consider a small dose increment (do not exceed max). If response is good, continue current dose.";
        } else if (actualStatus === "SUBTHERAPEUTIC") {
            theoppInterpretation = `The measured level of ${actualLevelNum.toFixed(1)} mg/L is below the target threshold of ${activeTargetRange.split(" - ")[0]} mg/L. The current dose may fail to provide adequate bronchodilation.`;
            theoppRecommendation = simDoseNum > 0 
                ? `Review patient adherence. If compliance is confirmed, titrate the dose upward. Your simulation shows that a new dose of ${simDoseNum}mg every ${simIntervalNum}h is expected to raise the level to ${simulatedTheoppCss.toFixed(1)} mg/L. If response is good, continue current dose.`
                : "Review patient compliance and smoking status. If the patient is asymptomatic or response good, a dose adjustment may not be immediately required, but close monitoring is advised.";
        } else if (actualStatus === "SUPRATHERAPEUTIC") {
            if (actualLevelNum > 25) {
                theoppInterpretation = `⚠️ CRITICAL TOXICITY RISK: The measured level (${actualLevelNum.toFixed(1)} mg/L) drastically exceeds the upper safety margin. Severe risk of sinus tachycardia, tachyarrhythmias, severe vomiting, and seizures.`;
                theoppRecommendation = "HOLD THEOPHYLLINE/AMINOPHYLLINE IMMEDIATELY. Check an ECG to monitor for arrhythmias. Check serum potassium (Theophylline toxicity causes hypokalemia). Re-check serum levels in 12-24 hours before re-initiating at a significantly reduced dose.";
            } else {
                theoppInterpretation = `The measured level (${actualLevelNum.toFixed(1)} mg/L) is elevated above the safety target (${activeTargetRange} mg/L), increasing the risk of mild toxicities like nausea, insomnia, and mild tachycardia.`;
                theoppRecommendation = "Withhold the next scheduled dose and decrease the ongoing maintenance dose. Carefully review the chart for newly introduced enzyme inhibitors (e.g., Ciprofloxacin, Erythromycin or Allopurinol ( ≥600 mg/day)).";
            }
        }
    }

    return (
        <section style={{ padding: '20px', background: '#1e272e', border: '2px solid #3498db', borderRadius: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                <button 
                    type="button"
                    className="no-print" 
                    onClick={() => {
                                    const actualUserEmail = user && user.email ? user.email : 'anonymous-user';
                                    const trackingLabel = `${actualUserEmail}-printed-Theophylline`;
                                    logUserActivityQuietly(trackingLabel); 
                                    window.print();
                                }} 
                    style={{
                    padding: '10px 20px',
                    backgroundColor: '#27ae60', 
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
                    }}
                >
                    🖨️ Print PDF Report
                </button>
            </div>
            <h2 style = {{textAlign: 'center', color: '#3498db'}}>Theophylline TDM Worksheet</h2>
            <details style={{
                backgroundColor: '#1E293B',
                border: '1px solid #38BDF8',
                borderRadius: '8px',
                padding: '10px 15px',
                marginBottom: '20px',
                cursor: 'pointer'
            }}>
                <summary style={{ color: '#38BDF8', fontWeight: 'bold' }}>
                ⏱️ Check Clinical Sampling Protocol (Click to expand)
                </summary>
                <div style={{ marginTop: '12px', padding: '15px', background: 'rgba(0,0,0,0.15)', borderRadius: '8px', border: '1px solid #3d4e5d', fontSize: '0.9rem', color: '#F8FAFC' }}>
    
                    {/* SECTION 1: DETAILED SAMPLING SCHEDULE TABLE */}
                    <div style={{ marginBottom: '15px' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#00d2ff', textTransform: 'uppercase', display: 'block', marginBottom: '6px', letterSpacing: '0.5px' }}>
                            ⏱️ Clinical Sampling & Stabilization Protocols
                        </span>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                            <tbody>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                    <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#fff', width: '35%', verticalAlign: 'top' }}>👶 Neonates:</td>
                                    <td style={{ padding: '8px 0', color: '#bdc3c7', lineHeight: '1.4' }}>
                                        Draw **2 hours** after the first loading dose (LD). Follow up with maintenance monitoring after **4 – 7 days**.
                                    </td>
                                </tr>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                    <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#fff', verticalAlign: 'top' }}>🧑 Infants to Geriatric:</td>
                                    <td style={{ padding: '8px 0', color: '#bdc3c7', lineHeight: '1.4' }}>
                                        • Draw **30 min post-first LD** (if peak verification desired).<br />
                                        • Check **12 – 24 hours** after initiation of maintenance dose (MD).<br />
                                        • Routine check: **3 days after initiation**, then every 1 – 3 days.
                                    </td>
                                </tr>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                    <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#fff' }}>Continuous Infusion:</td>
                                    <td style={{ padding: '8px 0', color: '#bdc3c7' }}>Draw sample **12 – 24 hours** after starting the infusion administration.</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#3498db' }}>Standard Trough:</td>
                                    <td style={{ padding: '8px 0', color: '#fff' }}>Draw exactly **30 minutes or immediately before** the next scheduled dose.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* SECTION 2: INDICATION METRICS & MOLAR CONVERSION FACTORS */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: '12px', marginBottom: '15px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                        {/* Indication Reference Ranges */}
                        <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px', borderRadius: '6px', fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <span style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#2ecc71', textTransform: 'uppercase', display: 'block' }}>🎯 Therapeutic Targets (mg/L):</span>
                            <div>🫁 **Asthma / COPD:** <span style={{color: '#fff', fontWeight: 'bold'}}>10 - 20 mg/L</span></div>
                            <div>👶 **Neonatal Apnea / Bradycardia:** <span style={{color: '#fff', fontWeight: 'bold'}}>5 - 10 mg/L</span></div>
                            <div>💨 **Neonatal Ventilator Weaning:** <span style={{color: '#fff', fontWeight: 'bold'}}>5 - 20 mg/L</span></div>
                        </div>

                        {/* Laboratory Conversion Tooltip */}
                        <div style={{ background: 'rgba(0, 210, 255, 0.05)', border: '1px solid rgba(0, 210, 255, 0.15)', padding: '10px', borderRadius: '6px', display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
                            <div style={{ fontWeight: 'bold', color: '#00d2ff', fontSize: '0.75rem', textTransform: 'uppercase' }}>🧪 Molar Conversion Engine:</div>
                            <div style={{ fontSize: '0.85rem', marginTop: '6px', color: '#fff', fontWeight: '500' }}>
                                 mg/L x 5.55 = mcmol/L
                            </div>
                            <span style={{ fontSize: '0.65rem', color: '#95a5a6', marginTop: '4px' }}>*Utilize this conversion when aligning with specific systemic lab readouts.</span>
                        </div>
                    </div>

                    {/* SECTION 3: STRATIFIED TOXICITY WARNING BARRIERS */}
                    <div style={{ background: 'rgba(231, 76, 60, 0.08)', borderLeft: '4px solid #e74c3c', padding: '10px', borderRadius: '0 6px 6px 0' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#e74c3c', textTransform: 'uppercase', display: 'block' }}>
                            ⚠️ Toxicity Escalation Limits
                        </span>
                        <div style={{ fontSize: '0.85rem', color: '#ecf0f1', marginTop: '4px', lineHeight: '1.4' }}>
                            • <strong style={{ color: '#ff7675' }}>Mild Toxicity (&gt; 20 mg/L):</strong> Nausea, vomiting, diarrhea, headache, restlessness, and mild tachycardia.<br />
                            • <strong style={{ color: '#e74c3c' }}>Severe Toxicity (&gt; 35 mg/L):</strong> High risk of intractable seizures, severe cardiac arrhythmias (SVT, VT), hypotension, and hypokalemia.
                        </div>
                    </div>

                </div>
            </details>
            <h3 style={{ color: '#3498db', marginTop: 0, fontSize: '1.2rem' }}>
                Step 1: Population-Based Clearance (With Interaction Factors)
            </h3>

            {/* Anthropometrics Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '15px' }}>
                <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Gender:</label>
                    <select value={gender} onChange={(e)=>setGender(e.target.value)} style={inputStyle}>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                    </select>
                </div>
                <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Actual Wt (kg):</label>
                    <input type="number" value={weight} onChange={(e)=>setWeight(e.target.value)} style={inputStyle} />
                </div>
                <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Height (cm) <span style={{color:'#95a5a6', fontWeight:'normal'}}>(Opt)</span>:</label>
                    <input type="number" value={height} onChange={(e)=>setHeight(e.target.value)} placeholder="None" style={inputStyle} />
                </div>
                <div style={{ textAlign: 'center', background: 'rgba(0,0,0,0.15)', borderRadius: '6px', padding: '4px' }}>
                    <span style={{ fontSize: '0.65rem', color: '#bdc3c7', display: 'block' }}>WEIGHT ENGINE</span>
                    <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#f1c40f' }}>{weightToUse.toFixed(1)} kg</span>
                    <span style={{ fontSize: '0.6rem', color: '#ecf0f1', display: 'block' }}>
                        {isUsingIBW ? "Using IBW" : heightNum > 0 ? "Using Actual" : "⚠️ Actual (No Ht)"}
                    </span>
                </div>
            </div>

            {/* Dropdowns for TDM 8.png Interaction Table */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '15px' }}>
                <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Clinical Condition:</label>
                    <select value={selectedCondition} onChange={(e)=>setSelectedCondition(e.target.value)} style={inputStyle}>
                        {Object.keys(CL_FACTORS.conditions).map(c => <option key={c} value={c}>{c} (×{CL_FACTORS.conditions[c]})</option>)}
                    </select>
                </div>
                <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Enzyme Inducers:</label>
                    <select value={selectedInducer} onChange={(e)=>setSelectedInducer(e.target.value)} style={inputStyle}>
                        {Object.keys(CL_FACTORS.drugInducers).map(d => <option key={d} value={d}>{d} (×{CL_FACTORS.drugInducers[d]})</option>)}
                    </select>
                </div>
                <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Enzyme Inhibitors:</label>
                    <select value={selectedInhibitor} onChange={(e)=>setSelectedInhibitor(e.target.value)} style={inputStyle}>
                        {Object.keys(CL_FACTORS.drugInhibitors).map(d => <option key={d} value={d}>{d} (×{CL_FACTORS.drugInhibitors[d]})</option>)}
                    </select>
                </div>
            </div>

            {/* Total Output Window */}
            <div style={{ background: '#fdf6e3', padding: '12px', borderRadius: '6px', fontSize: '0.85rem', color: '#2c3e50' }}>
                <strong>Math Formula:</strong> 0.04 L/kg/hr × {weightToUse.toFixed(1)} kg 
                {totalMultiplier !== 1 && ` × (${condFactor} × ${inducerFactor} × ${inhibitorFactor} Factors)`} = 
                <strong style={{ color: '#d35400', fontSize: '1.05rem', marginLeft: '5px' }}>{finalCL.toFixed(2)} L/hr</strong>
            </div>
            <div style={{ padding: '25px', background: '#fff', border: '2px solid #1abc9c', borderRadius: '12px', marginTop: '20px' }}>
                <h3 style={{ color: '#16a085', marginTop: 0, fontSize: '1.2rem' }}>
                    Step 2: Clinical Indication & Target Range
                </h3>
                
                <div style={{ marginBottom: '15px' }}>
                    <label style={{ fontWeight: 'bold', fontSize: '0.85rem', display: 'block', marginBottom: '6px', color: '#2c3e50' }}>
                        Clinical Indication:
                    </label>
                    <select 
                        value={indication} 
                        onChange={(e) => setIndication(e.target.value)}
                        style={{ width: '100%', padding: '10px', background: '#ecf0f1', border: '1px solid #bdc3c7', color: '#2c3e50', borderRadius: '5px', fontWeight: '500' }}
                    >
                        {Object.keys(THEOPHYLLINE_TARGETS).map(ind => (
                            <option key={ind} value={ind}>
                                {ind}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Target Range Display Window */}
                <div style={{ background: '#e8f8f5', padding: '9px', borderRadius: '8px', textAlign: 'center', border: '1px solid #1abc9c', marginTop: '10px' }}>
                    <span style={{ fontSize: '0.85rem', color: '#16a085', fontWeight: 'bold' }}>
                        Recommended Therapeutic Target:
                    </span>
                    <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#1abc9c', marginTop: '4px' }}>
                        {getTargetRange()} <small style={{ fontSize: '0.9rem' }}>mg/L</small>
                    </div>
                </div>
            </div>
            <div style={{ padding: '25px', background: '#34495e', border: '2px solid #e67e22', borderRadius: '12px', marginTop: '20px' }}>
                <h3 style={{ color: '#e67e22', marginTop: 0, fontSize: '1.2rem' }}>
                    Step 3: Dosage Prediction & Target Suitability
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                    {/* Formulation Selection */}
                    <div>
                        <label style={{ fontWeight: 'bold', fontSize: '0.8rem', display: 'block', marginBottom: '5px' }}>Formulation (Salt S):</label>
                        <select value={saltType} onChange={(e) => setSaltType(e.target.value)} style={inputStyle}>
                            <option value="theophylline">Theophylline (S = 1.0)</option>
                            <option value="aminophylline">Aminophylline (S = 0.8)</option>
                        </select>
                    </div>
                    {/* Current Dose Input */}
                    <div>
                        <label style={{ fontWeight: 'bold', fontSize: '0.8rem', display: 'block', marginBottom: '5px' }}>Current Dose (mg/dose):</label>
                        <input 
                            type="number" 
                            value={currentDose} 
                            onChange={(e) => setCurrentDose(e.target.value)} 
                            placeholder="e.g., 200"
                            style={inputStyle} 
                        />
                    </div>
                    {/* Interval Input */}
                    <div>
                        <label style={{ fontWeight: 'bold', fontSize: '0.8rem', display: 'block', marginBottom: '5px' }}>Interval (hours):</label>
                        <input 
                            type="number" 
                            value={interval} 
                            onChange={(e) => setInterval(e.target.value)} 
                            placeholder="e.g., 12"
                            style={inputStyle} 
                        />
                    </div>
                </div>

                {/* Predicted Concentration Display Window */}
                <div style={{ background: 'rgba(0,0,0,0.2)', padding: '15px', borderRadius: '8px', textAlign: 'center', border: `1px solid ${suitability.color}` }}>
                    <span style={{ fontSize: '0.75rem', color: '#bdc3c7', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                        ESTIMATED BASELINE LEVEL Css:
                    </span>
                    <div style={{ fontSize: '2.2rem', fontWeight: 'bold', color: suitability.color, margin: '5px 0' }}>
                        {predictedTheoppCss > 0 ? predictedTheoppCss.toFixed(1) : '0.0'} <span style={{ fontSize: '1rem' }}>mg/L</span>
                    </div>
                    
                    {/* Evaluation Badge */}
                    <div style={{ 
                        fontSize: '0.75rem', 
                        fontWeight: 'bold', 
                        color: '#fff', 
                        background: suitability.color, 
                        display: 'inline-block', 
                        padding: '4px 11px', 
                        borderRadius: '12px',
                        textTransform: 'uppercase'
                    }}>
                        {suitability.label}
                    </div>
                </div>

                {/* Math Proof Window for Clinical Review */}
                <div style={{ background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '6px', fontSize: '0.75rem', color: '#bdc3c7', marginTop: '12px' }}>
                    <strong>Math Proof:</strong> ({currentDoseNum}mg × {S} Salt × 1.0 F) / ({finalCL.toFixed(2)} L/hr × {intervalNum}hr) = {predictedTheoppCss.toFixed(1)} mg/L
                </div>
            </div>
            <div style={{ padding: '20px', background: '#34495e', border: '2px solid #9b59b6', borderRadius: '12px', marginTop: '20px' }}>
                <h3 style={{ color: '#9b59b6', marginTop: 0, fontSize: '1.2rem' }}>
                    Step 4: Clinical Correlation & Metabolic Deviation
                </h3>

                <div style={{ marginBottom: '15px' }}>
                    <label style={{ fontWeight: 'bold', fontSize: '0.85rem', display: 'block', marginBottom: '6px', color: '#fff' }}>
                        Actual Lab Measured Level (mg/L):
                    </label>
                    <input 
                        type="number" 
                        value={measuredLevel} 
                        onChange={(e) => setMeasuredLevel(e.target.value)} 
                        placeholder="Enter exact lab concentration result"
                        style={inputStyle} 
                    />
                </div>

                {actualLevelNum > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '15px' }}>
                        {/* Deviation Box */}
                        <div style={{ background: 'rgba(0,0,0,0.25)', padding: '15px', borderRadius: '8px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)' }}>
                            <div style={{ fontSize: '0.7rem', color: '#bdc3c7', fontWeight: 'bold', textTransform: 'uppercase' }}>Metabolic Deviation</div>
                            <div style={{ fontSize: '1.6rem', fontWeight: 'bold', color: theoppDeviation > 0 ? '#e74c3c' : '#3498db', margin: '4px 0' }}>
                                {theoppDeviation > 0 ? '+' : ''}{theoppDeviation.toFixed(1)}%
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#bdc3c7' }}>vs. Population Baseline</div>
                        </div>

                        {/* Individualized Clearance Box */}
                        <div style={{ background: 'rgba(0,0,0,0.3)', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '0.7rem', color: '#95a5a6' }}>INDIVIDUAL CL VS <small style = {{frontSize: '0.9rem',color: '#33d930'}}>POPULATION CL</small>  </div>
                            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#fff' }}>
                                {individualTheoppCL.toFixed(2)} <small style={{ fontSize: '0.7rem' }}>L/hr</small> VS <small style = {{color: '#33d930'}}>{finalCL.toFixed(2)} L/hr</small>
                            </div>
                        </div>

                        {/* Metabolism Status Summary Banner */}
                        <div style={{ gridColumn: 'span 2', padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', borderLeft: `5px solid ${theoppMetabolism.color}` }}>
                            <div style={{ fontWeight: 'bold', color: theoppMetabolism.color, fontSize: '0.9rem' }}>
                                {theoppMetabolism.text}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#bdc3c7', marginTop: '3px', lineHeight: '1.4' }}>
                                {theoppMetabolism.note}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {/* STEP 5: DOSE TITRATION SIMULATOR (INDIVIDUAL PATIENT HIGH-PRECISION MODEL) */}
            <div style={{ padding: '25px', background: '#1b1f23', border: '2px solid #2ecc71', borderRadius: '12px', marginTop: '20px' }}>
                <h3 style={{ color: '#2ecc71', marginTop: 0, fontSize: '1.2rem' }}>
                    Step 5: Dose Titration Simulator (Sandbox Mode)
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', alignItems: 'center' }}>
                    {/* Simulation Inputs Panel */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div>
                            <label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#bdc3c7', display: 'block', marginBottom: '4px' }}>
                                Proposed New Dose (mg/dose):
                            </label>
                            <input 
                                type="number" 
                                value={simDose}
                                onChange={(e) => setSimDose(e.target.value)}
                                placeholder="e.g., 250"
                                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                                disabled={!measuredLevel || !currentDose}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#bdc3c7', display: 'block', marginBottom: '4px' }}>
                                Proposed Interval (hours):
                            </label>
                            <input 
                                type="number" 
                                value={simInterval}
                                onChange={(e) => setSimInterval(e.target.value)}
                                placeholder="e.g., 12"
                                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                                disabled={!measuredLevel || !currentDose}
                            />
                        </div>
                        <p style={{ fontSize: '0.7rem', color: '#bdc3c7', margin: '4px 0 0 0', fontStyle: 'italic' }}>
                            *Titrate slowly in 50mg increments to minimize cardiovascular or CNS side effects.
                        </p>
                    </div>

                    {/* Live Simulation Projection Window */}
                    <div style={{ background: 'rgba(0,0,0,0.3)', padding: '20px', borderRadius: '10px', textAlign: 'center', border: `1px solid ${simSuitability.color || '#34495e'}` }}>
                        <span style={{ fontSize: '0.7rem', color: '#bdc3c7', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                            SIMULATED STEADY STATE (Css):
                        </span>
                        <div style={{ fontSize: '2.4rem', fontWeight: 'bold', color: '#fff', margin: '6px 0' }}>
                            {simulatedTheoppCss > 0 ? simulatedTheoppCss.toFixed(1) : '0.0'} <span style={{ fontSize: '1rem', fontWeight: 'normal', color: '#bdc3c7' }}>mg/L</span>
                        </div>
                        
                        {/* Target Match Status Badge */}
                        {simulatedTheoppCss > 0 && (
                            <div style={{ 
                                fontSize: '0.7rem', 
                                fontWeight: 'bold', 
                                color: '#fff', 
                                background: simSuitability.color, 
                                display: 'inline-block', 
                                padding: '4px 12px', 
                                borderRadius: '10px',
                                textTransform: 'uppercase',
                                marginBottom: '8px'
                            }}>
                                {simSuitability.label}
                            </div>
                        )}
                        
                        <div style={{ fontSize: '0.65rem', color: '#95a5a6', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '6px' }}>
                            Engine Model: {actualLevelNum > 0 ? "🎯 Personalized Patient Kinetic Model" : "⚠️ Standard Population Model"}
                        </div>
                    </div>
                </div>

                {/* VISUAL SPECTRUM PROGRESS SLIDER TRACK BAR */}
                {simulatedTheoppCss > 0 && (() => {
                    const rangeStr = getTargetRange(); // e.g. "5 - 15"
                    const [minTarget, maxTarget] = rangeStr.split(" - ").map(Number);
                    
                    // Standard max scale boundary cap set to 30 mg/L context landscape
                    const maxChartScale = 30;
                    const leftPercent = (minTarget / maxChartScale) * 100;
                    const widthPercent = ((maxTarget - minTarget) / maxChartScale) * 100;
                    const pinPercent = (simulatedTheoppCss / maxChartScale) * 100;

                    return (
                        <div style={{ padding: '15px', background: '#111416', borderRadius: '8px', border: '1px solid #2c3539', marginTop: '20px' }}>
                            <div style={{ position: 'relative', height: '14px', background: '#2c3e50', borderRadius: '7px', margin: '15px 0 15px 0', overflow: 'visible' }}>
                                
                                {/* TARGET THERAPEUTIC SHADOW HIGHLIGHT */}
                                <div style={{ 
                                    position: 'absolute', 
                                    left: `${Math.max(0, Math.min(90, leftPercent))}%`, 
                                    width: `${Math.max(10, Math.min(100, widthPercent))}%`, 
                                    height: '100%', 
                                    background: 'rgba(46, 204, 113, 0.22)', 
                                    borderLeft: '1px dashed #2ecc71', 
                                    borderRight: '1px dashed #2ecc71',
                                    borderRadius: '4px'
                                }} />

                                {/* ACTIVE PINPOINT INDICATOR NODE */}
                                <div style={{
                                    position: 'absolute',
                                    left: `${Math.max(0, Math.min(98, pinPercent))}%`,
                                    transform: 'translateX(-50%)',
                                    width: '18px',
                                    height: '18px',
                                    top: '-2px',
                                    background: '#f1c40f',
                                    borderRadius: '50%',
                                    boxShadow: '0 0 12px #f1c40f',
                                    border: '2px solid #fff',
                                    transition: 'left 0.2s ease-out',
                                    zIndex: 10
                                }} />
                            </div>

                            {/* GRAPH SCALING LABELS */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.62rem', color: '#7f8c8d', padding: '0 2px', marginBottom: '12px' }}>
                                <span>0 mg/L</span>
                                <span style={{ color: '#2ecc71', fontWeight: 'bold' }}>Target Spec: {rangeStr} mg/L</span>
                                <span>30 mg/L</span>
                            </div>

                            {/* CLEAR LINEAR CALCULATED RATIO TRACE AUDIT FOOTER */}
                            <div style={{ fontSize: '0.68rem', color: '#57606f', borderTop: '1px solid rgba(255,255,255,0.03)', paddingTop: '8px', lineHeight: '1.4', textAlign: 'left' }}>
                                {actualLevelNum > 0 ? (
                                    <>
                                        Formula Predict Css: [(Proposed Dose / Proposed Interval) / (Current Dose / Current Interval)] × Measured Level<br />
                                        Individual Patient Linear Rate Ratio Trace: [({simDoseNum} mg / {simIntervalNum}h) / ({currentDoseNum} mg / {intervalNum}h)] × {measuredLevel} mg/L = {simulatedTheoppCss.toFixed(1)} mg/L
                                    </>
                                ) : (
                                    <>
                                        Formula Predict Css: (Proposed Dose × S × F) / (Population Multiplier CL × Proposed Interval)<br />
                                        Multiplicative Estimate Reference Trace: ({simDoseNum}mg × {S} × 1.0) / ({finalCL.toFixed(2)} L/hr × {simIntervalNum}hr)
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })()}
            </div>

            {/* STEP 6 UI CONTAINER BOX */}
            <div style={{ 
                marginTop: '25px', 
                padding: '20px', 
                background: '#1b1f23', 
                borderRadius: '12px', 
                borderLeft: `8px solid ${getHeaderColor()}`, 
                boxShadow: '0 4px 15px rgba(0,0,0,0.4)'
            }}>
                <h4 style={{ color: getHeaderColor(), margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Step 6: Clinical Interpretation & Action Plan
                </h4>

                {/* Assessment Block */}
                <div style={{ marginBottom: '15px' }}>
                    <div style={{ color: '#95a5a6', fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                        Pharmacokinetic Assessment:
                    </div>
                    <div style={{ color: '#ecf0f1', fontSize: '0.95rem', marginTop: '4px', lineHeight: '1.5' }}>
                        {actualLevelNum > 0 ? (
                            <span>
                                <span style={{color: getHeaderColor(), fontWeight: 'bold', fontSize: '0.85rem'}}> 
                                    {actualStatus}: &nbsp;
                                </span>
                                <span>
                                    {theoppInterpretation}
                                </span> 
                            </span>
                        ) : (
                            <span style={{ color: '#7f8c8d', fontStyle: 'italic' }}>Awaiting lab results to formulate clinical assessment.</span>
                        )}
                    </div>
                </div>

                {/* Proposed Plan Block */}
                <div style={{ padding: '15px', background: 'rgba(255,255,255,0.04)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.02)' }}>
                    <div style={{ color: '#2ecc71', fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                        Proposed Pharmacist Care Plan:
                    </div>
                    <div style={{ color: '#fff', fontSize: '0.95rem', marginTop: '6px', lineHeight: '1.5' }}>
                        {actualLevelNum > 0 ? theoppRecommendation : "Recommendation will generate automatically once a lab level is recorded."}
                    </div>
                </div>
            </div>

            {/* HANDBOOK DISCLAIMER LEGEND FOOTER FOR MEDICAL FILING */}
            <p style={{ margin: '15px 0 0 0', fontSize: '0.62rem', color: '#7f8c8d', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.3' }}>
                *Evaluation derived from official Clinical Pharmacokinetics Handbook criteria. Patient-specific clinical conditions and absolute biomarker trends must always override automated population estimates prior to final intervention execution.
            </p>
        </section>
        
    )

}
const inputStyle = {
    width: '80%',
    padding: '8px',
    marginTop: '5px',
    borderRadius: '4px',
    border: '1px solid #ccc'
};
export default TheophyllineCalculator;